import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutomationFlow,
  AutomationFlowInput,
  AutomationPreview,
  AutomationRun,
  createAutomation,
  deleteAutomation,
  disableAutomation,
  duplicateAutomation,
  enableAutomation,
  listAutomationRuns,
  listAutomations,
  previewAutomation,
  runAutomation,
  updateAutomation,
} from '../api/client';

const defaultFlow: AutomationFlowInput = {
  name: '',
  description: '',
  status: 'disabled',
  trigger: {
    type: 'weekly_schedule',
    day_of_week: 'wednesday',
    time: '17:00',
    timezone: 'Asia/Jerusalem',
  },
  condition: {
    type: 'customer_debt_age',
    debt_age_days: 60,
    minimum_amount: 0,
  },
  action: {
    type: 'send_customer_message',
    message_body: 'שלום {{customer_name}}, סך החוב הפתוח שלך הוא ₪{{total_open_debt}}, מתוכו ₪{{aged_debt_amount}} חוב ישן. נשמח להסדיר.',
    include_payment_link: true,
  },
};

const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const statuses = ['draft', 'enabled', 'disabled', 'archived'];

function flowToInput(flow: AutomationFlow): AutomationFlowInput {
  return {
    name: flow.name,
    description: flow.description ?? '',
    status: flow.status === 'enabled' ? 'disabled' : flow.status,
    trigger: flow.trigger,
    condition: flow.condition,
    action: flow.action,
  };
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function Automations() {
  const { t } = useTranslation();
  const [flows, setFlows] = useState<AutomationFlow[]>([]);
  const [selected, setSelected] = useState<AutomationFlow | null>(null);
  const [draft, setDraft] = useState<AutomationFlowInput>(defaultFlow);
  const [preview, setPreview] = useState<AutomationPreview | null>(null);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const selectedRuns = useMemo(() => runs.filter((run) => run.flow_id === selected?.id), [runs, selected]);

  const loadFlows = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listAutomations({ status: statusFilter || undefined, q: query || undefined });
      setFlows(response.data.items);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlows();
  }, []);

  const resetForm = () => {
    setSelected(null);
    setDraft(defaultFlow);
    setPreview(null);
    setRuns([]);
    setError('');
    setNotice('');
  };

  const selectFlow = async (flow: AutomationFlow) => {
    setSelected(flow);
    setDraft(flowToInput(flow));
    setPreview(null);
    setNotice('');
    setError('');
    try {
      const response = await listAutomationRuns(flow.id);
      setRuns(response.data.items);
    } catch {
      setRuns([]);
    }
  };

  const saveFlow = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (selected) {
        const response = await updateAutomation(selected.id, draft);
        setSelected(response.data);
        setNotice(t('automations.updated'));
      } else {
        const response = await createAutomation(draft);
        setSelected(response.data);
        setNotice(t('automations.created'));
      }
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const previewFlow = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await previewAutomation(selected ? { automation_id: selected.id } : { flow: draft });
      setPreview(response.data);
      if (selected) {
        const refreshed = await listAutomations({ status: statusFilter || undefined, q: query || undefined });
        setFlows(refreshed.data.items);
        setSelected(refreshed.data.items.find((item) => item.id === selected.id) ?? selected);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to preview automation');
    } finally {
      setSaving(false);
    }
  };

  const enableSelected = async () => {
    if (!selected || !preview?.review_token) {
      setError(t('automations.previewRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await enableAutomation(selected.id, preview.review_token);
      setSelected(response.data);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to enable automation');
    } finally {
      setSaving(false);
    }
  };

  const disableSelected = async (flow: AutomationFlow) => {
    setSaving(true);
    try {
      const response = await disableAutomation(flow.id);
      if (selected?.id === flow.id) setSelected(response.data);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to disable automation');
    } finally {
      setSaving(false);
    }
  };

  const duplicateSelected = async (flow: AutomationFlow) => {
    setSaving(true);
    try {
      const response = await duplicateAutomation(flow.id);
      await loadFlows();
      await selectFlow(response.data);
      setNotice('Automation duplicated.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to duplicate automation');
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = async (flow: AutomationFlow) => {
    if (!window.confirm(t('automations.deleteConfirm'))) return;
    setSaving(true);
    try {
      await deleteAutomation(flow.id);
      if (selected?.id === flow.id) resetForm();
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete automation');
    } finally {
      setSaving(false);
    }
  };

  const runSelected = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      await runAutomation(selected.id);
      const response = await listAutomationRuns(selected.id);
      setRuns(response.data.items);
      await loadFlows();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to run automation');
    } finally {
      setSaving(false);
    }
  };

  const setDraftField = (path: string, value: string | number | boolean) => {
    setDraft((current) => {
      const next: any = structuredClone(current);
      const parts = path.split('.');
      let target = next;
      for (const part of parts.slice(0, -1)) target = target[part];
      target[parts[parts.length - 1]] = value;
      return next;
    });
    setPreview(null);
  };

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('automations.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('automations.subtitle')}</p>
        </div>
        <button
          onClick={resetForm}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          {t('automations.create')}
        </button>
      </header>

      {(error || notice) && (
        <div className={`rounded-md border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
          {error || notice}
        </div>
      )}

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 sm:flex-row">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('common.search')}
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">{t('automations.allStatuses')}</option>
              {statuses.map((status) => (
                <option key={status} value={status}>{t(`automations.${status}`)}</option>
              ))}
            </select>
            <button
              onClick={loadFlows}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.filter')}
            </button>
          </div>

          {loading ? (
            <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">{t('common.loading')}</div>
          ) : flows.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">{t('automations.noAutomations')}</div>
          ) : (
            <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
              <div className="divide-y divide-gray-200">
                {flows.map((flow) => (
                  <button
                    key={flow.id}
                    onClick={() => selectFlow(flow)}
                    className={`block w-full px-4 py-4 text-left hover:bg-gray-50 ${selected?.id === flow.id ? 'bg-primary-50' : ''}`}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-medium text-gray-900">{flow.name}</h2>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${flow.status === 'enabled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {t(`automations.${flow.status}`)}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                          {flow.trigger_summary} · {flow.condition_summary} · {flow.action_summary}
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          {t('automations.lastRun')}: {formatDate(flow.last_run_at)} · {t('automations.nextRun')}: {formatDate(flow.next_run_at)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {flow.status === 'enabled' ? (
                          <span onClick={(event) => { event.stopPropagation(); disableSelected(flow); }} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700">
                            {t('automations.disable')}
                          </span>
                        ) : null}
                        <span onClick={(event) => { event.stopPropagation(); duplicateSelected(flow); }} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700">
                          {t('automations.duplicate')}
                        </span>
                        <span onClick={(event) => { event.stopPropagation(); deleteSelected(flow); }} className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600">
                          {t('common.delete')}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold text-gray-900">{selected ? t('automations.edit') : t('automations.create')}</h2>
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="text-gray-700">{t('automations.flowName')}</span>
                <input value={draft.name} onChange={(e) => setDraftField('name', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">{t('automations.description')}</span>
                <textarea value={draft.description ?? ''} onChange={(e) => setDraftField('description', e.target.value)} className="mt-1 min-h-16 w-full rounded-md border border-gray-300 px-3 py-2" />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-700">{t('automations.dayOfWeek')}</span>
                  <select value={draft.trigger.day_of_week} onChange={(e) => setDraftField('trigger.day_of_week', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                    {days.map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-700">{t('automations.time')}</span>
                  <input type="time" value={draft.trigger.time} onChange={(e) => setDraftField('trigger.time', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-700">{t('automations.debtAgeDays')}</span>
                  <input type="number" min={1} value={draft.condition.debt_age_days} onChange={(e) => setDraftField('condition.debt_age_days', Number(e.target.value))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-700">{t('automations.minimumAmount')}</span>
                  <input type="number" min={0} value={draft.condition.minimum_amount ?? 0} onChange={(e) => setDraftField('condition.minimum_amount', Number(e.target.value))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
                </label>
              </div>

              <label className="block text-sm">
                <span className="text-gray-700">{t('automations.messageBody')}</span>
                <textarea value={draft.action.message_body} onChange={(e) => setDraftField('action.message_body', e.target.value)} className="mt-1 min-h-24 w-full rounded-md border border-gray-300 px-3 py-2" />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={draft.action.include_payment_link ?? false} onChange={(e) => setDraftField('action.include_payment_link', e.target.checked)} />
                {t('automations.includePaymentLink')}
              </label>

              <div className="flex flex-wrap gap-2 pt-2">
                <button disabled={saving} onClick={saveFlow} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                  {t('common.save')}
                </button>
                <button disabled={saving} onClick={previewFlow} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50">
                  {t('automations.preview')}
                </button>
                {selected && selected.status !== 'enabled' ? (
                  <span
                    className={!preview ? 'cursor-not-allowed' : ''}
                    title={!preview ? t('automations.previewRequired') : t('automations.enableTooltip')}
                  >
                    <button
                      disabled={saving || !preview}
                      onClick={enableSelected}
                      className="rounded-md border border-green-300 px-4 py-2 text-sm font-medium text-green-700 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      {t('automations.enable')}
                    </button>
                  </span>
                ) : null}
                {selected ? (
                  <button disabled={saving} onClick={runSelected} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50">
                    {t('automations.runNow')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {preview ? (
            <div className="rounded-md border border-gray-200 bg-white p-4">
              <h2 className="text-base font-semibold text-gray-900">{t('automations.preview')}</h2>
              <p className="mt-2 text-sm text-gray-600">{t('automations.matchedCustomers')}: {preview.matched_count}</p>
              <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap">{preview.message_preview}</div>
              {preview.sample_customers.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">{t('automations.noMatches')}</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {preview.sample_customers.map((customer) => (
                    <li key={customer.customer_id} className="rounded-md border border-gray-100 p-2 text-sm">
                      <span className="font-medium text-gray-900">{customer.customer_name}</span>
                      <span className="block text-gray-500">
                        ₪{customer.total_open_debt.toLocaleString()} total · ₪{customer.aged_debt_amount.toLocaleString()} old · {customer.oldest_open_debt_days} days
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {selected ? (
            <div className="rounded-md border border-gray-200 bg-white p-4">
              <h2 className="text-base font-semibold text-gray-900">{t('automations.runs')}</h2>
              {selectedRuns.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">{t('common.noResults')}</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {selectedRuns.map((run) => (
                    <div key={run.id} className="rounded-md border border-gray-100 p-3 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="font-medium text-gray-900">{run.status}</span>
                        <span className="text-gray-500">{formatDate(run.started_at)}</span>
                      </div>
                      <p className="mt-1 text-gray-600">
                        {run.matched_count} matched · {run.action_succeeded_count} succeeded · {run.action_failed_count} failed
                      </p>
                      {run.results?.filter((result) => result.action_status === 'failed').map((result) => (
                        <p key={`${run.id}-${result.customer_id}`} className="mt-2 text-red-600">
                          {result.customer_name}: {result.error_message}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </aside>
      </section>
    </div>
  );
}
